import { Award, Lock } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useTranslations } from '@/hooks/use-translations';
import { trackEvent } from '@/modules/google-analytics/analytic/analytics';

import { useCertificationStatus } from '../hooks/useCertificationStatus';

interface CertificationButtonProps {
  caseId: string;
  masterclassId: string | null;
  userId: string | null;
  onStartCertification: () => void;
  onTestClose?: () => void;
}

export const CertificationButton = ({
  caseId,
  masterclassId,
  userId,
  onStartCertification,
  onTestClose,
}: CertificationButtonProps) => {
  const { t } = useTranslations();
  const { status, loading, refetch } = useCertificationStatus(caseId, userId, masterclassId);
  const [showResultModal, setShowResultModal] = useState(false);

  // Refresh status when test closes
  const handleTestClose = () => {
    if (onTestClose) onTestClose();
    void refetch();
  };

  if (loading) {
    return (
      <Button
        disabled
        variant="outline"
        className="mt-2 w-full border-livelinx-lightgray100 bg-livelinx-grey100"
      >
        <Award className="mr-2 h-4 w-4" />
        {t('common.buttons.loading')}
      </Button>
    );
  }

  if (status.passed) {
    return (
      <Button
        variant="outline"
        className="mt-2 w-full border-livelinx-green bg-livelinx-green50 text-livelinx-green200 hover:bg-livelinx-green100"
        onClick={(e) => {
          e.stopPropagation();
          trackEvent('Certification', 'Review Certification', caseId, null);
          onStartCertification();
        }}
      >
        <Award className="mr-2 h-4 w-4" />
        {t('certification.button.reviewCertification', {
          score: (status.bestScore || 0).toFixed(0),
        })}
      </Button>
    );
  }

  const handleLockedClick = () => {
    if (!status.caseCompleted) {
      toast({
        title: t('certification.notifications.caseNotCompleted.title'),
        description: t('certification.notifications.caseNotCompleted.description'),
        variant: 'default',
        duration: 5000,
      });
    }
  };

  if (!status.caseCompleted) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mt-2 w-full">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  trackEvent(
                    'Certification',
                    'Click when certification is still locked',
                    caseId,
                    null
                  );
                  handleLockedClick();
                }}
                variant="outline"
                className="w-full border-livelinx-lightgray100 bg-livelinx-grey100 text-livelinx-grey200"
              >
                <Lock className="mr-2 h-4 w-4" />
                {t('certification.button.certificationLocked')}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('certification.tooltips.completeCase')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (status.attemptsUsed >= status.maxAttempts) {
    return (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          trackEvent('Certification', 'Click when attempts are exhausted', caseId, null);
          toast({
            title: t('certification.notifications.attemptsExhausted.title'),
            description: t('certification.notifications.attemptsExhausted.description', {
              max: status.maxAttempts,
            }),
            variant: 'destructive',
            duration: 5000,
          });
        }}
        variant="outline"
        className="mt-2 w-full border-livelinx-red bg-livelinx-red/10 text-livelinx-red"
      >
        <Award className="mr-2 h-4 w-4" />
        {t('certification.button.attemptsExhausted', {
          used: status.attemptsUsed,
          max: status.maxAttempts,
        })}
      </Button>
    );
  }

  const remainingAttempts = status.maxAttempts - status.attemptsUsed;

  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        trackEvent('Certification', 'Start Certification', caseId, null);
        onStartCertification();
      }}
      variant="outline"
      className="mt-2 w-full border-livelinx-purple200 bg-livelinx-purple50 text-livelinx-purple200 hover:bg-livelinx-purple75"
    >
      <Award className="h-4 w-4" />
      {status.attemptsUsed === 0
        ? t('certification.button.takeCertification')
        : `${t('certification.button.newAttempt', { used: status.attemptsUsed, max: status.maxAttempts })}`}
    </Button>
  );
};
